import { useState, useEffect } from "react";
import { Globe, Users, TrendingUp, Shield, ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from "recharts";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const META = {
  care: { label: "Care", title: "The Healers", subtitle: "AI built for human wellbeing", color: "#34d399", question: "Is care AI reaching the people who need it most?" },
  utility: { label: "Utility", title: "The Builders", subtitle: "AI for productivity and efficiency", color: "#60a5fa", question: "Are we building tools for everyone, or tools for the privileged?" },
  creative: { label: "Creative", title: "The Creators", subtitle: "AI enabling artistic expression", color: "#a78bfa", question: "Is AI democratizing creativity, or concentrating it?" },
  extraction: { label: "Extraction", title: "The Extractors", subtitle: "AI designed to extract value", color: "#f87171", question: "Where is the line between insight and surveillance?" },
};

const PIE_COLORS = ["#34d399", "#60a5fa", "#a78bfa", "#f87171", "#f59e0b", "#06b6d4", "#ec4899"];

export const OrientationAnatomy = ({ orientation, entityType }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const meta = META[orientation] || META.utility;

  useEffect(() => {
    setLoading(true);
    const params = entityType ? `?entity_type=${entityType}` : "";
    axios.get(`${API}/anatomy/${orientation}${params}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [orientation, entityType]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 animate-pulse" style={{ background: `${meta.color}15` }}>
          <div className="w-4 h-4 rounded-full" style={{ background: meta.color }} />
        </div>
        <p className="text-sm text-slate-400">Loading {meta.label} anatomy...</p>
      </div>
    </div>
  );
  if (!data) return null;

  const founderData = Object.entries(data.founders || {}).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));
  const govData = Object.entries(data.governance || {}).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid={`anatomy-${orientation}`}>
      {/* Hero */}
      <div className="rounded-2xl py-8 px-6 text-center" style={{ background: `linear-gradient(135deg, ${meta.color}08 0%, ${meta.color}04 100%)`, border: `1px solid ${meta.color}20` }}>
        <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background: `${meta.color}15` }}>
          <div className="w-5 h-5 rounded-full" style={{ background: meta.color }} />
        </div>
        <h2 className="text-2xl font-bold mb-1">{meta.title}</h2>
        <p className="text-xs font-bold uppercase tracking-[0.2em] mb-2" style={{ color: meta.color }}>{meta.subtitle}</p>
        <p className="text-sm text-slate-400">{data.total.toLocaleString()} apps ({data.pct_of_global}% of {data.total_global.toLocaleString()} global)</p>
      </div>

      {/* Key stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold" style={{ color: meta.color }}>{data.total.toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Total Apps</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{data.countries_count || data.countries?.length || 0}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Countries</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-pink-400">{data.comparison?.female_pct || 0}%</p>
          <p className="text-[10px] text-muted-foreground mt-1">Female-Founded</p>
          <p className="text-[10px] text-muted-foreground/60">vs {data.comparison?.global_female_pct}% global</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-violet-400">{data.comparison?.mixed_pct || 0}%</p>
          <p className="text-[10px] text-muted-foreground mt-1">Mixed Teams</p>
          <p className="text-[10px] text-muted-foreground/60">vs {data.comparison?.global_mixed_pct}% global</p>
        </div>
      </div>

      {/* Growth trend + Country distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Year trend */}
        {data.yearly_trend?.length > 0 && (
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-bold mb-3">Growth over time</h3>
            <div style={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.yearly_trend}>
                  <defs>
                    <linearGradient id={`grad-${orientation}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={meta.color} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={meta.color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="year" tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
                  <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                  <Area type="monotone" dataKey="count" stroke={meta.color} strokeWidth={2.5} fill={`url(#grad-${orientation})`} dot={{ r: 3, fill: meta.color }} animationDuration={1500} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Top countries */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Top countries</h3>
          <div className="space-y-2">
            {data.countries?.slice(0, 8).map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-20 truncate">{c.country}</span>
                <div className="flex-1 h-2 bg-white/[0.04] rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(c.count / (data.countries[0]?.count || 1)) * 100}%`, background: meta.color, opacity: 1 - i * 0.08 }} />
                </div>
                <span className="text-xs font-bold text-muted-foreground w-10 text-right">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Monetization + Governance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Monetization models</h3>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.monetization?.slice(0, 6) || []} dataKey="count" nameKey="model" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={9}>
                  {(data.monetization || []).slice(0, 6).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Founder composition</h3>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={founderData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
                <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={70} stroke="rgba(255,255,255,0.1)" />
                <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                <Bar dataKey="value" fill={meta.color} radius={[0, 4, 4, 0]} animationDuration={1000} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Notable apps */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-bold mb-3">Notable {meta.label} apps</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {data.notable_apps?.slice(0, 12).map((app, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-white/[0.03]">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta.color }} />
              <span className="text-xs font-medium text-slate-300 truncate">{app.product_name}</span>
              {app.country && <span className="text-[10px] text-muted-foreground ml-auto flex-shrink-0">{app.country}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Provocative question */}
      <div className="text-center py-6 rounded-2xl" style={{ background: `${meta.color}06`, border: `1px solid ${meta.color}15` }}>
        <p className="text-lg font-bold text-white max-w-lg mx-auto italic">"{meta.question}"</p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-[10px] text-slate-500">
        <span>{typeof window !== "undefined" ? window.location.origin.replace("https://", "") : ""}</span>
        <span>&copy; {new Date().getFullYear()} AI Orientation Observatory</span>
      </div>
    </div>
  );
};

export default OrientationAnatomy;
